import type { SetterOrUpdater } from 'recoil';
import type { OptionOf } from '~/components/molecule/Dropdown';
import type {
  FormBuilder_Node,
  FormBuilder_NodeFragment,
  FormBuilder_ScreenNode,
} from '~/graphql/types';
import deleteNode from '../../../../utils/deleteNode';
import type { CopyScreenNodeState } from '../../../../state/copyScreenNode';
import type { ComponentLevel } from '~/styles/constants';

const text = {
  deleteScreen: 'Pagina verwijderen',
  deleteSubmitScreen: 'Verzendpagina verwijderen',
  deleteSubmitScreenWarning: `Weet je het zeker dat je dit wilt verwijderen? Zonder verzendpagina zal er geen data opgeslagen worden en zal je ook geen contactgebeurtenissen ontvangen. Doe dit alleen voor statische pagina's`,
  deleteEvent: 'Gebeurtenis verwijderen',
};

/**
 * Used to display a modal if there is a need
 */
export type ModalProps = {
  /** Should the modal be shown */
  shown: boolean;

  /** Modal header */
  header?: string;

  /** Modal body */
  body?: string;

  /** Modal appearance */
  level?: ComponentLevel;

  /** Function to call on confirm */
  onConfirm: () => void;
};

type MenuArgs = {
  /** Type of node that options belong to */
  nodeType: FormBuilder_Node['__typename'];

  /** Is the node the start node */
  isStartNode: boolean;

  /** Length of screen nodes in the form */
  screenNodesLength: number;

  /** Is the node the submit screen in the basic form */
  isSubmitScreenNode?: boolean;

  /** Used to update nodes state */
  setNodesState: SetterOrUpdater<Array<FormBuilder_NodeFragment>>;

  /** Used to copy screen nodes */
  setCopyScreenNodeState: SetterOrUpdater<CopyScreenNodeState>;
};

type OptionPayload = (values: {
  nodes: Array<FormBuilder_Node>;

  /** Id of the node that was clicked */
  nodeId: string;

  /** Used if you want to show a confirm modal before running the action */
  setModalOptions: (modalProps: ModalProps) => void;
}) => void;

type Option = OptionOf<OptionPayload>;

/**
 * Returns options for menus
 */
const getMenuOptions = (args: MenuArgs): Array<Option> => {
  switch (args.nodeType) {
    case 'FormBuilder_EventNode':
      if (args.isStartNode) return [];
      return [getDeleteOption({ args, header: text.deleteEvent })];

    case 'FormBuilder_ScreenNode':
      if (args.screenNodesLength === 1) return [getCopyOption({ args })];

      if (args.isSubmitScreenNode)
        return [
          getDeleteOption({
            args,
            header: text.deleteSubmitScreen,
            description: text.deleteSubmitScreenWarning,
          }),
        ];

      return [
        getCopyOption({ args }),
        getDeleteOption({ args, header: text.deleteScreen }),
      ];

    default:
      return [];
  }
};

const getDeleteOption = ({
  args,
  header,
  description,
}: {
  args: MenuArgs;
  header: string;
  description?: string;
}): Option => ({
  key: 'delete-node',
  label: 'Verwijderen',
  icon: { name: 'trashcan' },
  type: 'DANGER',
  payload: ({ nodeId, setModalOptions }) => {
    if (!nodeId) return;

    setModalOptions({
      shown: true,
      header,
      body: description ?? 'Weet je het zeker?',
      level: 'danger',
      onConfirm: () =>
        args.setNodesState(prev => deleteNode({ nodeId, nodes: prev })),
    });
  },
});

const getCopyOption = ({ args }: { args: MenuArgs }): Option => ({
  key: 'copy-screen-node',
  label: 'Kopiëren',
  icon: { name: 'copy' },
  payload: ({ nodeId, nodes }) => {
    if (!nodeId) return;

    const copiedNode = nodes.find(
      ({ id, __typename }) =>
        id === nodeId && __typename === 'FormBuilder_ScreenNode',
    ) as FormBuilder_ScreenNode | null;

    if (copiedNode) {
      args.setCopyScreenNodeState({
        copiedNode,
        nodesBeforeCopy: nodes,
      });
    }
  },
});

export default getMenuOptions;
